import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Badge,
  useColorModeValue,
  Icon,
  Flex,
  Spacer,
  useToast,
  Progress,
  Avatar,
  CircularProgress,
  CircularProgressLabel
} from '@chakra-ui/react';
import {
  FiBook,
  FiPlay,
  FiAward,
  FiBell,
  FiClock,
  FiTrendingUp,
  FiTarget,
  FiCalendar,
  FiFileText
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import Layout from '../shared/Layout';
import { useTranslation } from '../../contexts/TranslationContext';

const StudentDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();
  const { t, formatDate } = useTranslation();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchDashboardData();
    fetchAbsences();
  }, []);

  const fetchAbsences = async () => {
    try {
      const response = await axiosInstance.get('/absences/my');
      if (response.data.success) {
        setAbsences(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching student absences:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/student/dashboard');
      setDashboardData(response.data.data);
    } catch (error) {
      toast({
        title: t('messages.error'),
        description: error.message || t('messages.somethingWentWrong'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'blue', progress }) => (
    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
      <CardBody>
        <Flex>
          <Box flex="1">
            <Stat>
              <StatLabel fontSize="sm" color="gray.500">{title}</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold">{value}</StatNumber>
              {subtitle && (
                <StatHelpText fontSize="sm">{subtitle}</StatHelpText>
              )}
            </Stat>
            {progress !== undefined && (
              <Progress
                value={progress}
                colorScheme={color}
                size="sm"
                borderRadius="md"
                mt={2}
              />
            )}
          </Box>
          <Box>
            <Icon as={icon} boxSize={8} color={`${color}.500`} />
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );

  const CourseCard = ({ course, onClick }) => (
    <Card
      bg={cardBg}
      borderColor={borderColor}
      borderWidth="1px"
      cursor="pointer"
      onClick={onClick}
      _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
      transition="all 0.2s"
    >
      <CardBody>
        <VStack spacing={3} align="stretch">
          <Flex align="center">
            <Box flex="1">
              <Text fontWeight="bold" noOfLines={1}>{course.course?.name}</Text>
              <Text fontSize="sm" color="gray.500">{course.course?.teacherName}</Text>
            </Box>
            <CircularProgress
              value={course.progress?.overallProgress || 0}
              size="50px"
              color={`${course.progress?.overallProgress > 70 ? 'green' : course.progress?.overallProgress > 40 ? 'yellow' : 'red'}.400`}
            >
              <CircularProgressLabel fontSize="xs">
                {course.progress?.overallProgress || 0}%
              </CircularProgressLabel>
            </CircularProgress>
          </Flex>

          <VStack spacing={2} align="stretch">
            <Flex justify="space-between" fontSize="sm">
              <Text color="gray.500">وقت المشاهدة:</Text>
              <Text>{Math.floor((course.progress?.totalTimeSpent || 0) / 60)} دقيقة</Text>
            </Flex>
            <Flex justify="space-between" fontSize="sm">
              <Text color="gray.500">آخر وصول:</Text>
              <Text>
                {course.progress?.lastAccessedAt
                  ? new Date(course.progress.lastAccessedAt).toLocaleDateString('ar-SA')
                  : 'لم يتم الوصول بعد'
                }
              </Text>
            </Flex>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );

  const NotificationItem = ({ notification }) => (
    <Flex align="center" p={3} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
      <Icon
        as={FiBell}
        color={notification.read ? 'gray.400' : 'blue.500'}
        mr={3}
      />
      <Box flex="1">
        <Text fontWeight={notification.read ? 'normal' : 'bold'} fontSize="sm">
          {notification.title}
        </Text>
        <Text fontSize="xs" color="gray.500" noOfLines={1}>
          {notification.message}
        </Text>
      </Box>
      <Text fontSize="xs" color="gray.400">
        {new Date(notification.createdAt).toLocaleDateString('ar-SA')}
      </Text>
    </Flex>
  );

  if (loading) {
    return (
      <Layout>
        <Container maxW="7xl" py={8}>
          <Text>{t('common.loading')}</Text>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box>
            <Heading size="lg" mb={2}>{t('dashboard.student.title')}</Heading>
            <Text color="gray.500">{t('dashboard.student.subtitle')}</Text>
          </Box>

          {/* Statistics Cards */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6}>
            <StatCard
              title="الدورات المسجلة"
              value={dashboardData?.progress?.totalCourses || 0}
              subtitle={`${dashboardData?.progress?.completedCourses || 0} مكتملة`}
              icon={FiBook}
              color="blue"
              progress={dashboardData?.progress?.overallCompletionRate || 0}
            />
            <StatCard
              title="ساعات المشاهدة"
              value={Math.floor((dashboardData?.progress?.totalTimeSpent || 0) / 3600)}
              subtitle="ساعة إجمالية"
              icon={FiClock}
              color="green"
            />
            <StatCard
              title="الاختبارات المكتملة"
              value={dashboardData?.progress?.qcmAttempts || 0}
              subtitle={`${dashboardData?.progress?.qcmPassed || 0} ناجح`}
              icon={FiAward}
              color="purple"
              progress={dashboardData?.progress?.averageQcmScore || 0}
            />
            <StatCard
              title="الإشعارات"
              value={dashboardData?.notifications?.unreadCount || 0}
              subtitle="غير مقروءة"
              icon={FiBell}
              color="orange"
            />
          </Grid>

          {/* Quick Actions */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
            <Card
              bg={cardBg}
              borderColor={borderColor}
              borderWidth="1px"
              cursor="pointer"
              onClick={() => navigate('/student/courses')}
              _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
              transition="all 0.2s"
            >
              <CardBody>
                <VStack spacing={3}>
                  <Icon as={FiBook} boxSize={8} color="blue.500" />
                  <Text fontWeight="bold" textAlign="center">دوراتي</Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">عرض جميع الدورات</Text>
                </VStack>
              </CardBody>
            </Card>

            <Card
              bg={cardBg}
              borderColor={borderColor}
              borderWidth="1px"
              cursor="pointer"
              onClick={() => navigate('/student/qcms')}
              _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
              transition="all 0.2s"
            >
              <CardBody>
                <VStack spacing={3}>
                  <Icon as={FiFileText} boxSize={8} color="green.500" />
                  <Text fontWeight="bold" textAlign="center">الاختبارات</Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">الاختبارات المتاحة</Text>
                </VStack>
              </CardBody>
            </Card>

            <Card
              bg={cardBg}
              borderColor={borderColor}
              borderWidth="1px"
              cursor="pointer"
              onClick={() => navigate('/student/uploads')}
              _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
              transition="all 0.2s"
            >
              <CardBody>
                <VStack spacing={3}>
                  <Icon as={FiTrendingUp} boxSize={8} color="purple.500" />
                  <Text fontWeight="bold" textAlign="center">ملفاتي</Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">الملفات المرفوعة</Text>
                </VStack>
              </CardBody>
            </Card>

            <Card
              bg={cardBg}
              borderColor={borderColor}
              borderWidth="1px"
              cursor="pointer"
              onClick={() => navigate('/student/notifications')}
              _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
              transition="all 0.2s"
            >
              <CardBody>
                <VStack spacing={3}>
                  <Icon as={FiBell} boxSize={8} color="orange.500" />
                  <Text fontWeight="bold" textAlign="center">الإشعارات</Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">آخر التحديثات</Text>
                </VStack>
              </CardBody>
            </Card>
          </Grid>

          {/* Main Content */}
          <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
            {/* Enrolled Courses */}
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardHeader>
                <Flex>
                  <Heading size="md">دوراتي</Heading>
                  <Spacer />
                  <Button
                    size="sm"
                    leftIcon={<FiBook />}
                    onClick={() => navigate('/student/courses')}
                  >
                    عرض الكل
                  </Button>
                </Flex>
              </CardHeader>
              <CardBody>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  {dashboardData?.enrolledCourses?.slice(0, 4).map((course, index) => (
                    <CourseCard
                      key={index}
                      course={course}
                      onClick={() => navigate(`/student/courses/${course.course._id}`)}
                    />
                  )) || <Text color="gray.500">لا توجد دورات مسجلة</Text>}
                </Grid>
              </CardBody>
            </Card>

            {/* Notifications & Quick Info */}
            <VStack spacing={6} align="stretch">
              {/* Recent Notifications */}
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                <CardHeader>
                  <Flex>
                    <Heading size="md">الإشعارات الحديثة</Heading>
                    <Spacer />
                    <Button
                      size="sm"
                      leftIcon={<FiBell />}
                      onClick={() => navigate('/student/notifications')}
                    >
                      عرض الكل
                    </Button>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack spacing={3} align="stretch">
                    {dashboardData?.notifications?.recent?.slice(0, 3).map((notification, index) => (
                      <NotificationItem key={index} notification={notification} />
                    )) || <Text color="gray.500">لا توجد إشعارات</Text>}
                  </VStack>
                </CardBody>
              </Card>

              {/* Absence History Card */}
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                <CardHeader>
                  <Flex align="center">
                    <Icon as={FiClock} mr={2} color="red.500" />
                    <Heading size="md">سجل الغيابات</Heading>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack spacing={3} align="stretch">
                    {absences.length > 0 ? (
                      absences.slice(0, 5).map((abs, index) => (
                        <Flex key={index} align="center" p={3} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                          <Box flex="1">
                            <Text fontWeight="bold" fontSize="sm">{abs.matier}</Text>
                            <Text fontSize="xs" color="gray.500">{abs.alertMessage}</Text>
                          </Box>
                          <Badge colorScheme="red" variant="subtle">
                            {new Date(abs.date).toLocaleDateString('fr-FR')}
                          </Badge>
                        </Flex>
                      ))
                    ) : (
                      <Text color="gray.500" fontSize="sm" textAlign="center">لا توجد غيابات مسجلة</Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </Grid>
        </VStack>
      </Container >
    </Layout >
  );
};

export default StudentDashboard;

